import { type Filament, type Printer, type Supply, formatBRL } from '@deizi/shared';
import { type ReactNode, useState } from 'react';
import { SEARCH_FROM, matches } from '../../components/Combobox';
import { Link } from 'react-router';
import { Icon } from '../../components/Icon';
import { Swatch as BaseSwatch } from '../../components/Swatch';
import { Modal, Sheet } from '../../components/Overlays';
import { useIsPhone } from '../../lib/use-media';

/** Choice list in a dialog on desktop and a bottom sheet on the phone (boards QQ-Desktop / QQ-Celular). */
export function PickerOverlay({
  title,
  intro,
  onClose,
  footer,
  search,
  children,
}: {
  title: string;
  intro?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  /** A search box above the list (stays put while the list scrolls). */
  search?: ReactNode;
  children: ReactNode;
}) {
  const phone = useIsPhone();
  if (phone) {
    return (
      <Sheet label={title} onClose={onClose}>
        <h2>{title}</h2>
        {intro ? <p className="xs muted">{intro}</p> : null}
        {search}
        <div className="col" style={{ gap: 8, overflowY: 'auto', maxHeight: '60vh' }}>
          {children}
        </div>
        <button type="button" className="btn block" onClick={onClose}>
          Cancelar
        </button>
      </Sheet>
    );
  }
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          {footer}
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
        </>
      }
    >
      {intro ? <p className="sm muted">{intro}</p> : null}
      {search}
      <div className="col" style={{ gap: 8, overflowY: 'auto', maxHeight: '55vh' }}>
        {children}
      </div>
    </Modal>
  );
}

/** Filament color dot (the shared `Swatch`, by `color`). */
export function Swatch({ color, size = 18 }: { color: string | null; size?: number }) {
  return <BaseSwatch hex={color} size={size} />;
}

/** Search box for the picker lists, from `SEARCH_FROM` items; returns the box and the query. */
function usePickerSearch(count: number, label: string) {
  const [q, setQ] = useState('');
  const box =
    count >= SEARCH_FROM ? (
      <label className="search" style={{ maxWidth: 'none' }}>
        <Icon name="search" size={18} />
        <input
          aria-label={label}
          placeholder={label}
          role="searchbox"
          // oxlint-disable-next-line jsx-a11y/no-autofocus -- the picker opens to choose
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
    ) : null;
  return { box, q };
}

export function filamentSub(f: Filament): string {
  return `${formatBRL(f.pricePerKgCentavos)}/kg · ${f.material}`;
}

export function FilamentPicker({
  title,
  filaments,
  selectedId,
  material,
  onChoose,
  onClose,
}: {
  title: string;
  filaments: Filament[];
  selectedId: string | null;
  /** Material the slicer used: same-material filaments come first. */
  material: string | null;
  onChoose: (f: Filament) => void;
  onClose: () => void;
}) {
  const same = (f: Filament) =>
    material && f.material.toUpperCase() === material.toUpperCase() ? 0 : 1;
  const search = usePickerSearch(filaments.length, 'Buscar filamento');
  const list = filaments
    .filter((f) =>
      matches(
        { value: f.id, label: f.name, search: `${f.brand} ${f.material} ${f.colorName ?? ''}` },
        search.q,
      ),
    )
    .toSorted((a, b) => same(a) - same(b) || a.name.localeCompare(b.name));
  return (
    <PickerOverlay
      title={title}
      intro="O preço por kg entra no custo do material."
      onClose={onClose}
      search={search.box}
      footer={
        <Link className="btn g" to="/filamentos">
          Ver filamentos
        </Link>
      }
    >
      <div className="col" style={{ gap: 8 }} role="listbox" aria-label="Filamentos">
        {list.length === 0 ? (
          <p className="sm muted">
            {filaments.length ? (
              'Nenhum filamento com esse nome.'
            ) : (
              <>
                Nenhum filamento cadastrado. <Link to="/filamentos">Cadastrar</Link>
              </>
            )}
          </p>
        ) : null}
        {list.map((f) => {
          const on = f.id === selectedId;
          return (
            <button
              key={f.id}
              type="button"
              role="option"
              aria-selected={on}
              className={on ? 'pick on' : 'pick'}
              onClick={() => onChoose(f)}
            >
              <Swatch color={f.colorHex} size={20} />
              <span className="grow" style={{ minWidth: 0 }}>
                <span
                  className="trunc"
                  style={{ fontWeight: 500, display: 'block' }}
                  title={f.name}
                >
                  {f.name}
                </span>
                <span className="xs muted tab" style={{ display: 'block' }}>
                  {f.brand} · {filamentSub(f)}
                </span>
              </span>
              {on ? <Icon name="check" size={18} /> : null}
            </button>
          );
        })}
      </div>
    </PickerOverlay>
  );
}

export function printerSub(p: Printer): string {
  return `${formatBRL(p.costPerHour.low.total)}/h · ${p.powerLowWatts} W · depreciação, energia e manutenção`;
}

export function PrinterPicker({
  printers,
  selectedId,
  onChoose,
  onClose,
}: {
  printers: Printer[];
  selectedId: string | null;
  onChoose: (p: Printer) => void;
  onClose: () => void;
}) {
  return (
    <PickerOverlay
      title="Impressora"
      intro={
        <>
          Custo da máquina por hora de impressão. Mude os valores em{' '}
          <Link to="/impressoras">Impressoras</Link>.
        </>
      }
      onClose={onClose}
    >
      <div className="col" style={{ gap: 8 }} role="listbox" aria-label="Impressoras">
        {printers.map((p) => {
          const on = p.id === selectedId;
          return (
            <button
              key={p.id}
              type="button"
              role="option"
              aria-selected={on}
              className={on ? 'pick on' : 'pick'}
              onClick={() => onChoose(p)}
            >
              <span className="grow" style={{ minWidth: 0 }}>
                <span className="trunc" style={{ fontWeight: 500, display: 'block' }}>
                  {p.name} · {p.model}
                </span>
                <span className="xs muted tab" style={{ display: 'block' }}>
                  {printerSub(p)}
                </span>
              </span>
              {on ? <Icon name="check" size={18} /> : null}
            </button>
          );
        })}
      </div>
    </PickerOverlay>
  );
}

export function SupplyPicker({
  supplies,
  chosen,
  onAdd,
  onClose,
}: {
  supplies: Supply[];
  chosen: { supplyId: string; qty: string }[];
  onAdd: (s: Supply) => void;
  onClose: () => void;
}) {
  const all = supplies.filter((s) => s.category !== 'packaging');
  const search = usePickerSearch(all.length, 'Buscar insumo');
  const list = all.filter((s) => matches({ value: s.id, label: s.name }, search.q));
  return (
    <PickerOverlay
      title="Adicionar insumo"
      intro={
        <>
          Custo por unidade. Cadastre novos em <Link to="/insumos">Insumos</Link>.
        </>
      }
      onClose={onClose}
      search={search.box}
    >
      {list.length === 0 ? (
        <p className="sm muted">
          {all.length ? 'Nenhum insumo com esse nome.' : 'Nenhum insumo cadastrado.'}
        </p>
      ) : null}
      {list.map((s) => {
        const have = chosen.find((c) => c.supplyId === s.id);
        return (
          <button key={s.id} type="button" className="pick" onClick={() => onAdd(s)}>
            <span className="grow" style={{ fontWeight: 500 }}>
              {s.name}
            </span>
            <span className="tab">{formatBRL(s.unitCostCentavos)} / un.</span>
            <span className="badge">{have ? `${have.qty} por un.` : 'Adicionar'}</span>
          </button>
        );
      })}
    </PickerOverlay>
  );
}
